import React, { useEffect, useRef, useState } from 'react';

// Free-form numeric input that doesn't fight the keyboard.
//
// Controlled number inputs that parse on every keystroke snap "-", "1.", or ""
// back to 0, which makes typing negatives and decimals impossible. This keeps
// a local string draft while the field is focused and only commits parseable
// values; native spinner carats stay available (step/min/max pass through).
const NumericInput = ({
  value,
  onCommit,
  step = 1,
  min,
  max,
  className = 'op3-stepper',
  disabled = false,
  placeholder,
  title,
  ariaLabel,
  name
}) => {
  const [draft, setDraft] = useState(null);
  const commitTimer = useRef(null);
  const lastCommittedDraft = useRef(null);
  const skipNextBlur = useRef(false);
  const display = draft !== null ? draft : (value ?? '');
  const commitRaw = (raw) => {
    if (raw === '' || raw === '-' || raw === '.' || raw === '-.') return;
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && lastCommittedDraft.current !== raw) {
      lastCommittedDraft.current = raw;
      onCommit(parsed);
    }
  };

  useEffect(() => () => {
    if (commitTimer.current) clearTimeout(commitTimer.current);
  }, []);

  return (
    <input
      type="number"
      inputMode="decimal"
      name={name || (ariaLabel ? ariaLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') : undefined)}
      autoComplete="off"
      className={className}
      disabled={disabled}
      step={step}
      min={min}
      max={max}
      placeholder={placeholder}
      title={title}
      aria-label={ariaLabel}
      value={display}
      onChange={(event) => {
        const raw = event.target.value;
        setDraft(raw);
        if (commitTimer.current) clearTimeout(commitTimer.current);
        commitTimer.current = setTimeout(() => commitRaw(raw), 180);
      }}
      onBlur={(event) => {
        if (commitTimer.current) clearTimeout(commitTimer.current);
        if (!skipNextBlur.current) commitRaw(event.target.value);
        skipNextBlur.current = false;
        setDraft(null);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          if (commitTimer.current) clearTimeout(commitTimer.current);
          event.currentTarget.blur();
        }
        if (event.key === 'Escape') {
          if (commitTimer.current) clearTimeout(commitTimer.current);
          skipNextBlur.current = true;
          setDraft(null);
          event.currentTarget.blur();
        }
      }}
    />
  );
};

export default NumericInput;
